<script setup lang="ts">
import AppLogo from '~/components/logo/AppLogo.vue'

const { isAuthenticated, logout, user } = useSanctumAuth()
const router = useRouter()
const isOpen = ref(false)
const collapsed = ref(false)

const handleLogout = async () => {
  try {
    await logout()
  } catch (e) {
    console.error('Logout error:', e)
  }
  router.push('/login')
}

const items = computed(() => [
  [
    {
      label: 'Tableau de bord',
      type: 'label' as const
    },
    {
      label: 'Vue d\'ensemble',
      icon: 'i-heroicons-chart-bar',
      to: '/'
    },
    {
      label: 'Serveurs',
      icon: 'i-heroicons-server-stack',
      to: '/guilds'
    },
    {
      label: 'Salons',
      icon: 'i-heroicons-chat-bubble-left-right',
      to: '/channels'
    },
    {
      label: 'Simulateur Riven',
      icon: 'i-heroicons-sparkles',
      to: '/test'
    }
  ]
])

const userItems = computed(() => [
  [{
    label: user.value?.fullName || user.value?.username || 'devilbul',
    slot: 'account',
    disabled: true
  }],
  [{
    label: 'Déconnexion',
    color: 'error' as const,
    icon: 'i-heroicons-arrow-left-on-rectangle',
    onSelect: handleLogout
  }]
])
</script>

<template>
  <UButton
    icon="i-heroicons-bars-3"
    color="neutral"
    variant="ghost"
    class="lg:hidden fixed top-4 left-14 z-50 cursor-pointer"
    @click="isOpen = true"
  />

  <UDashboardSidebar
    id="app"
    v-model:open="isOpen"
    v-model:collapsed="collapsed"
    collapsible
    :ui="{ footer: 'border-t border-default', header: 'min-h-(--ui-admin-header-height) h-auto! px-0 w-full flex-row-reverse lg:flex-row' }"
    class="bg-accented/90 backdrop-blur-md shadow-2xl shadow-[#0E1729]/50"
    :class="{ 'min-w-75': !collapsed }"
  >
    <template #toggle="{ toggle }">
      <UButton icon="i-lucide-x" color="neutral" variant="ghost" class="lg:hidden cursor-pointer" @click="toggle" />
    </template>

    <template #header="{ collapsed, collapse }">
      <div
        class="flex justify-center py-4 gap-x-8 w-full transition-all"
        :class="[collapsed ? 'flex-col items-center justify-center gap-4' : 'flex-row items-center justify-between']"
      >
        <AppLogo :collapsed="collapsed" class="px-2" />
        <UButton
          :icon="collapsed ? 'i-lucide-panel-left-open' : 'i-lucide-panel-left-close'"
          color="neutral"
          variant="ghost"
          class="hidden lg:inline-flex cursor-pointer"
          @click="collapse?.(!collapsed)"
        />
      </div>
    </template>

    <template #default="{ collapsed }">
      <UNavigationMenu :collapsed="collapsed" :items="items" orientation="vertical" />
    </template>

    <template #footer="{ collapsed }">
      <div class="w-full flex items-center justify-between gap-x-2">
        <UDropdownMenu v-if="isAuthenticated" :items="userItems" :popper="{ placement: 'right-start' }" class="w-full">
          <UUser
            :name="collapsed ? '' : user?.fullName || user?.username || 'devilbul'"
            :description="collapsed ? '' : 'Staff Riven'"
            :avatar="{
              src: user?.avatarUrl || undefined,
              alt: user?.fullName || user?.username || 'devilbul',
              icon: 'i-fa6-solid-user'
            }"
            class="cursor-pointer w-full"
          />

          <template #account="{ item }">
            <div class="text-left px-1 py-0.5">
              <p class="text-[10px] text-muted">Connecté en tant que</p>
              <p class="truncate font-medium text-gray-900 dark:text-white">
                {{ item.label }}
              </p>
            </div>
          </template>
        </UDropdownMenu>

        <div v-else class="flex items-center gap-2 px-3 py-2 w-full text-xs text-muted">
          <UIcon name="i-heroicons-shield-check" class="size-4 text-primary" />
          <span v-if="!collapsed" class="font-medium">Riven Bot Admin v1.0.0</span>
        </div>
      </div>
    </template>
  </UDashboardSidebar>
</template>
